/**
 * A boundary for a feature the Hub can do without.
 *
 * Written for the two push components (Codex review of PR #1242, P1). They
 * became lazy so firebase/messaging could leave the bundle parsed before the
 * first render, and that handed them a failure mode they did not have as
 * static imports: a chunk that cannot be fetched rejects, and Suspense
 * catches suspension, not errors, so the rejection travels to the nearest
 * error boundary, which was the root AppErrorBoundary. A notification
 * listener nobody asked for could therefore replace a perfectly usable Hub
 * with the whole-app crash screen, on exactly the weak connections this work
 * exists to help.
 *
 * So: render nothing and carry on. The person loses push for this visit and
 * keeps the Hub, which is the right way round. The error is still reported,
 * because silently swallowing it would hide a chunk that is failing for
 * everybody.
 *
 * Load what goes inside it with lazyOptional() (lib/lazyRoute.ts), never
 * lazyRoute(). lazyRoute() answers a failed chunk by reloading the whole Hub
 * before this boundary ever sees the error, and spends the stale-chunk
 * cooldown doing it (PR #1242 refute round, P3). lazyOptional() hands the
 * failure straight here, which is what makes "render nothing and carry on"
 * true.
 *
 * NOT for anything a person is waiting on. A feature whose absence changes
 * what they can do needs to say so, not vanish.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureSentryException } from '../lib/sentry';

interface Props {
  /** Named in the Sentry report so a failing chunk is identifiable. */
  feature: string;
  children: ReactNode;
}

interface State {
  failed: boolean;
}

export default class OptionalFeatureBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureSentryException(error, {
      extra: {
        optionalFeature: this.props.feature,
        componentStack: info.componentStack ?? '(none)',
      },
    });
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}
